import { useEffect, useRef } from 'react';
import { TourStep } from '../types/TourTypes';

interface UseTourObjectiveProps {
  currentStep: TourStep | null;
  completedSteps: string[];
  onComplete: (stepId: string) => void;
}

/**
 * Hook que monitora o cumprimento dos objetivos de cada etapa do tour
 * Verifica automaticamente a cada 2 segundos se o objetivo foi cumprido
 * APENAS APÓS o usuário clicar no elemento destacado (elemento alvo)
 */
export const useTourObjective = ({
  currentStep,
  completedSteps,
  onComplete
}: UseTourObjectiveProps) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasInteractedRef = useRef<Set<string>>(new Set()); // Rastreia steps que tiveram interação

  useEffect(() => {
    if (!currentStep || completedSteps.includes(currentStep.id)) {
      // Limpa o intervalo se não há step atual ou já foi completado
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Salva contador inicial de integrações quando step começa
    if (currentStep.id === 'create_integration') {
      const savedCount = sessionStorage.getItem('tour_integrations_initial_count');
      if (!savedCount) {
        // Busca contagem atual e salva
        (async () => {
          try {
            const IntegrationBaseService = (await import('@/features/Integrations/services/integrationsBaseService')).IntegrationBaseService;

            const integrationsBase = await IntegrationBaseService.getAll({
              status: 'active'
            });

            sessionStorage.setItem('tour_integrations_initial_count', integrationsBase.length.toString());
            console.log('[useTourObjective] Saved initial integrations count:', integrationsBase.length);
          } catch (error) {
            console.error('[useTourObjective] Error saving initial integrations count:', error);
          }
        })();
      } else {
        console.log('[useTourObjective] Initial integrations count already saved:', savedCount);
      }
    }

    // Salva contador inicial de agentes quando step começa
    if (currentStep.id === 'install_agent') {
      const savedCount = sessionStorage.getItem('tour_agents_initial_count');
      if (!savedCount) {
        // Busca contagem atual e salva
        (async () => {
          try {
            const WorkspaceService = (await import('@/features/Workspaces/services/workspaceService')).WorkspaceService;
            const AgentBaseService = (await import('@/features/Agents/services/agentBaseService')).AgentBaseService;

            const products = await WorkspaceService.getAll();
            if (products && products.length > 0) {
              const response = await AgentBaseService.getByProductId(products[0].id);
              const count = response?.data?.length || 0;

              sessionStorage.setItem('tour_agents_initial_count', count.toString());
              console.log('[useTourObjective] Saved initial agents count:', count);
            }
          } catch (error) {
            console.error('[useTourObjective] Error saving initial agents count:', error);
          }
        })();
      } else {
        console.log('[useTourObjective] Initial agents count already saved:', savedCount);
      }
    }

    // Adiciona listener de clique no elemento alvo para marcar como interagido
    const targetElement = document.querySelector(currentStep.targetElement);
    console.log('[useTourObjective] Target element:', currentStep.targetElement, 'found:', !!targetElement);

    const handleElementClick = () => {
      console.log('[useTourObjective] Element clicked! Step:', currentStep.id);
      hasInteractedRef.current.add(currentStep.id);
      console.log('[useTourObjective] Interacted steps:', Array.from(hasInteractedRef.current));
    };

    // Listener para evento customizado de interação (mais confiável)
    const handleCustomInteraction = (event: CustomEvent) => {
      if (event.detail?.stepId === currentStep.id) {
        console.log('[useTourObjective] Custom interaction event! Step:', currentStep.id);
        hasInteractedRef.current.add(currentStep.id);
        console.log('[useTourObjective] Interacted steps:', Array.from(hasInteractedRef.current));

        // Se for o step do playground, salva o timestamp da interação
        if (currentStep.id === 'playground_chat') {
          sessionStorage.setItem('tour_playground_interaction_time', Date.now().toString());
          console.log('[useTourObjective] Saved playground interaction time');
        }
      }
    };

    // Listener para evento de agente instalado com sucesso
    const handleAgentInstalled = () => {
      if (currentStep.id === 'install_agent') {
        console.log('[useTourObjective] Agent installed successfully! Completing step immediately');
        onComplete(currentStep.id);
      }
    };

    // Listener para evento de produto criado com sucesso
    const handleWorkspaceCreated = () => {
      if (currentStep.id === 'create_workspace') {
        console.log('[useTourObjective] Workspace created successfully! Completing step immediately');
        onComplete(currentStep.id);
      }
    };

    // Listener para evento de documento uploadado com sucesso
    const handleDocumentUploaded = () => {
      if (currentStep.id === 'upload_knowledge') {
        console.log('[useTourObjective] Document uploaded successfully! Completing step immediately');
        onComplete(currentStep.id);
      }
    };

    // Listener para evento de integração criada com sucesso
    const handleIntegrationCreated = () => {
      if (currentStep.id === 'create_integration') {
        onComplete(currentStep.id);
      }
    };

    window.addEventListener('tour-step-interacted', handleCustomInteraction as EventListener);
    window.addEventListener('tour-agent-installed', handleAgentInstalled);
    window.addEventListener('workspace-created', handleWorkspaceCreated);
    window.addEventListener('tour-document-uploaded', handleDocumentUploaded);
    window.addEventListener('tour-integration-created', handleIntegrationCreated);

    if (targetElement) {
      targetElement.addEventListener('click', handleElementClick);
      console.log('[useTourObjective] Click listener added to element');
    } else {
      console.warn('[useTourObjective] Target element not found!');
    }

    // Verifica o objetivo a cada 5 segundos
    // EXCETO para create_workspace que usa apenas evento workspace-created
    // (2s estava gerando carga excessiva no backend — cada validator dispara
    //  múltiplas requisições em cascata)
    if (currentStep.id !== 'create_workspace') {
      intervalRef.current = setInterval(async () => {
        try {
          // Verifica novamente se não foi completado enquanto aguardava
          if (completedSteps.includes(currentStep.id)) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return;
          }

          // SÓ valida se o usuário já interagiu com o elemento
          const hasInteracted = hasInteractedRef.current.has(currentStep.id);
          console.log('[useTourObjective] Checking step:', currentStep.id, 'hasInteracted:', hasInteracted);

          if (!hasInteracted) {
            console.log('[useTourObjective] User has not interacted yet, skipping validation');
            return;
          }

          console.log('[useTourObjective] Running validator for step:', currentStep.id);
          const isCompleted = await currentStep.validator();
          console.log('[useTourObjective] Validator result:', isCompleted);

          // Verifica mais uma vez antes de completar
          if (isCompleted && !completedSteps.includes(currentStep.id)) {
            console.log('[useTourObjective] Calling onComplete for step:', currentStep.id);
            onComplete(currentStep.id);
          }
        } catch (error) {
          console.error('[useTourObjective] Validation error:', error);
        }
      }, 5000);
    } else {
      console.log('[useTourObjective] Step create_workspace uses event-based completion only (no polling)');
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Remove listener de clique
      if (targetElement) {
        targetElement.removeEventListener('click', handleElementClick);
      }

      // Remove listener de evento customizado
      window.removeEventListener('tour-step-interacted', handleCustomInteraction as EventListener);
      window.removeEventListener('tour-agent-installed', handleAgentInstalled);
      window.removeEventListener('workspace-created', handleWorkspaceCreated);
      window.removeEventListener('tour-document-uploaded', handleDocumentUploaded);
      window.removeEventListener('tour-integration-created', handleIntegrationCreated);
    };
  }, [currentStep, completedSteps, onComplete]);
};
